import {
    TableBody,
    TableCell,
    TableHead,
    Table,
    TableRow,
    TableSortLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TablePagination,
    TableContainer,
} from 'MaterialUIComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { React, moment, _ } from 'Imports';
import {
    VehicleCameraPairingResponse,
    VehicleCameraPairingRequest,
    VehicleRequest,
    EditVehicleCameraPairingRequest,
    TelematicProviderVehicleResponse,
} from '$Generated/api';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { IDeviceAssociationServiceInjectedProps, DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { PageHeader } from '$Components/Shared/PageHeader';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { SortDropdownItems } from '$Utilities/dataModelUtilities';
import { splitTreatmentNames, useGetSplitTreatment } from '$Utilities/split-io-sdk';
import { LiveVideo } from './components/LiveVideo';
import { DeviceAssociationExport } from './DeviceAssociationExport';
import { PairingDialog } from './components/PairingDialog';
import { VideoRequestDialog } from './components/VideoRequestDialog';
import { RequestVideoDrawer } from '$Components/VideoRecall/RequestVideoDrawer';
import ConfigurableFilterBar, { FilterConfig, FilterType } from '$Components/FilterComponents/ConfigurableFilterBar';
import { AlertMessage } from '$Components/Shared/AlertMessage';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { ConfirmLiveVideo } from './components/ConfirmLiveVideo';
import { TermsOfUse } from '$Components/Shared/TermsOfUse';
import { ConnectionStatusTooltip } from './components/ConnectionStatusTooltip';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import useHorizontalDragScroll from '$Utilities/hooks/useHorizontalDragScroll';
import { useMediaQuery } from 'react-responsive';
import { EllipsisItem, EllipsisPopperMenu } from '$Components/Shared/EllipsisPopperMenu';
import { FiltersButton } from '$Components/FilterComponents/Buttons';

interface IDeviceAssociationBaseProps {}

type DeviceAssociationProps = IDeviceAssociationBaseProps &
    IDeviceAssociationServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps &
    IConfigServiceInjectedProps;

interface IDeviceAssociationState {
    isTooltipOpen: boolean;
    serialFilter: string;
    selectedPairingStatusFilter: string[];
    selectedVehiclesFilter: string[];
    sort: string;
    sortOrder: 'asc' | 'desc';
    currentPageNumber: number;
    allItems: VehicleCameraPairingResponse[];
    filteredItems: VehicleCameraPairingResponse[];
    showPairing: boolean;
    showRequestVideo: boolean;
    device: any;
    cameraId: number | undefined;
    showDelete: boolean;
    deletePairingId: number | undefined;
    deletePairingIdTelematicDeviceId: string | undefined;
    disableAddRemoveButton: boolean;
    devicesLoaded: boolean;
    groupsFilter: string;
    openTooltips: boolean[];
    showLiveVideo: boolean;
    showConfirmLiveVideo: boolean;
    alertDriver: boolean;
    vehicleCameraPairingSelected: number | undefined;
    csvTitle: string;
    editParingId: number | undefined;
    gpsTrackerEnabled: boolean;
    openSnackBar: boolean;
    alertType: AlertTypes;
    alertMessage: string;
    deviceId: string | undefined;
    currentVehicleIdForEditing: string | undefined;
    rowsPerPage: number;
    encompassError: boolean;
}

const styles = require('pages/DeviceAssociation/DeviceAssociation.scss') as {
    main: string;
    filterBar: string;
    serialFilter: string;
    vehicleFilter: string;
    buttonContainer: string;
    addButton: string;
    card: string;
    sortLabel: string;
    endPairing: string;
    pickerContent: string;
    pickerLabel: string;
    select: string;
    scrollWindow: string;
    devicesTable: string;
    headerRow: string;
    bodyRow: string;
    endPairingEncompass: string;
    filterBarEncompass: string;
    headerCell: string;
    bodyCell: string;
    noAssociations: string;
    closeIcon: string;
    closeButtonSerialFilter: string;
    inputAdormentSerialFilter: string;
    dropdownIcon: string;
    clearIcon: string;
    dialog: string;
    optionsColumn: string;
    optionsEllipsisColumn: string;
    encompassOptionColumn: string;
    liveVideoDialog: string;
    onlineStreamaxStatus: string;
    infoButton: string;
    noSortingLabel: string;
    connectionStatusHeader: string;
    connectionStatusHeaderLabel: string;
    connectionStatusDate: string;
    dragScrollActive: string;
    minWidthHeaderCell: string;
    pinnedColumnCell: string;
    minWidthBodyCell: string;
    pairingFilter: string;
    filtersButton: string;
    mobileButtonContainer: string;
    liveVideoMobileDialog: string;
};

const _DeviceAssociation = (props: DeviceAssociationProps) => {
    const [deviceAssociationState, setDeviceAssociationState] = useState<IDeviceAssociationState>({
        isTooltipOpen: false,
        serialFilter: '',
        selectedPairingStatusFilter: [],
        selectedVehiclesFilter: [],
        sort: 'startDate',
        sortOrder: 'asc',
        currentPageNumber: 0,
        filteredItems: [],
        allItems: [],
        showPairing: false,
        device: { Id: '0' },
        cameraId: undefined,
        showDelete: false,
        deletePairingId: undefined,
        deletePairingIdTelematicDeviceId: undefined,
        disableAddRemoveButton: false,
        devicesLoaded: false,
        openTooltips: [],
        showLiveVideo: false,
        showConfirmLiveVideo: false,
        alertDriver: false,
        vehicleCameraPairingSelected: undefined,
        groupsFilter: '',
        csvTitle: 'DeviceAssociations',
        editParingId: undefined,
        gpsTrackerEnabled: false,
        showRequestVideo: false,
        openSnackBar: false,
        alertType: AlertTypes.Alert,
        alertMessage: '',
        deviceId: undefined,
        currentVehicleIdForEditing: undefined,
        rowsPerPage: 10,
        encompassError: false,
    });

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            await props.integrationPartnerData.getDevices();
            await handleDeviceMapping();
            await props.deviceAssociation.getAvailableDevices();
            await props.deviceAssociation.getUnpairedCameraDevices();
            const { availableDevicesResults } = props.deviceAssociation.getState();
            const items = availableDevicesResults.data ? availableDevicesResults.data : [];

            setDeviceAssociationState((prevState) => ({
                ...prevState,
                devicesLoaded: true,
                filteredItems: items,
                allItems: items,
            }));
        };

        fetchData();
    }, []);

    const reApplyFilters = () => {
        const { selectedVehiclesFilter, serialFilter, selectedPairingStatusFilter, showRequestVideo } = deviceAssociationState;
        // This is the sole path the allItems-watching effect below takes, including the background resync
        // triggered by a camera disconnect detected mid-preview. While that drawer is open the user isn't
        // interacting with this table at all, so keep them on whatever page they were viewing instead of
        // silently bumping them back to page 0 once they close it. Direct user actions (explicit sort/filter
        // clicks) call handleRequestSort themselves and are unaffected by this.
        handleRequestSort(undefined, applyFilters(selectedVehiclesFilter, serialFilter, selectedPairingStatusFilter), showRequestVideo);
    };

    const handleVehicleFilterChange = (vehicleIds: string[]): void => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            selectedVehiclesFilter: vehicleIds,
        }));
        // apply existing sort after filtering data
        handleRequestSort(undefined, applyFilters(vehicleIds, undefined, undefined));
    };

    const syncSelectedVehicleFilter = (selectedVehicleIds: string[], selectableVehiclesIds: string[]): void => {
        const filteredVehicleIds = selectedVehicleIds.filter((v) => selectableVehiclesIds.includes(v));
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            selectedVehiclesFilter: filteredVehicleIds,
        }));
    };

    const handleSerialFilterChange = (eventOrValue: any): void => {
        // Support both event object (old TextField) and direct string value (new ConfigurableFilterBar)
        const newSerial: string = typeof eventOrValue === 'string' ? eventOrValue : (eventOrValue?.target?.value ?? '');

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            serialFilter: newSerial,
        }));

        // apply existing sort after filtering data
        handleRequestSort(undefined, applyFilters(undefined, newSerial, undefined));
    };

    const handlePairingStatusFilterChange = (vehicleStatus: string[]): void => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            selectedPairingStatusFilter: vehicleStatus,
        }));

        handleRequestSort(undefined, applyFilters(undefined, undefined, vehicleStatus));
    };

    const handleMobileFiltersChange = (vehicles: string[], pairings: string[], newSerial: string): void => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            selectedPairingStatusFilter: pairings,
            serialFilter: newSerial,
            selectedVehiclesFilter: vehicles,
        }));

        handleRequestSort(undefined, applyFilters(vehicles, newSerial, pairings));
        setOpenFilterDevicesPanel(false);
    };

    const handleClearSerialFilterClick = () => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            serialFilter: '',
        }));

        // apply existing sort after filtering data
        handleRequestSort(undefined, applyFilters(undefined, ''));
    };

    const getDeviceAssociationFilterConfig = (): FilterConfig[] => {
        const disabled = vehicles.length === 0 || pairingStatuses.length === 0;

        return [
            {
                type: FilterType.Multiselect,
                key: 'vehicles',
                label: 'Paired Vehicle',
                items: vehicles,
                selectedValues: selectedVehiclesFilter,
                disabled: disabled,
                searchable: true,
            },
            {
                type: FilterType.Multiselect,
                key: 'pairingStatuses',
                label: 'Pairing Status',
                items: pairingStatuses,
                selectedValues: selectedPairingStatusFilter,
                disabled: disabled,
            },
            {
                type: FilterType.TextField,
                key: 'serialFilter',
                label: 'serialFilter',
                placeholder: 'Search by camera serial # ...',
                value: serialFilter,
                onClear: handleClearSerialFilterClick,
                disabled: disabled,
            },
        ];
    };

    const handleDeviceAssociationFilterChange = (filterKey: string, value: any): void => {
        if (filterKey === 'vehicles') {
            const updatedFilterItems = _.sortBy(value.map((v: any) => v.toString()));
            handleVehicleFilterChange(updatedFilterItems);
        } else if (filterKey === 'pairingStatuses') {
            const updatedFilterItems = _.sortBy(value.map((v: any) => v.toString()));
            handlePairingStatusFilterChange(updatedFilterItems);
        } else if (filterKey === 'serialFilter') {
            handleSerialFilterChange(value);
        }
    };

    const handleRequestSort = (field?: string, filteredItems?: VehicleCameraPairingResponse[], preservePage?: boolean): void => {
        const { sort, sortOrder } = deviceAssociationState;
        let isAsc: boolean = sortOrder === 'asc';

        if (!field) {
            // if no field specified, keep existing sort (i.e. a filter was changed)
            field = sort;
        } else if (field === sort) {
            // reverse current sort if same field selected
            isAsc = !isAsc;
        } else {
            // always sort new field ascending
            isAsc = true;
        }

        // if no items specified, use existing filtered items set (or fall back to all items)
        if (!filteredItems) {
            filteredItems = deviceAssociationState.filteredItems || deviceAssociationState.allItems;
        }

        // .sort() mutates in place -- when no filter is active, filteredItems here is the very same array
        // reference as allItems (applyFilters returns it untouched), which in turn can be the frozen array
        // straight from the Freezer cache. Sorting a copy avoids mutating that frozen array directly.
        const sortItems = [...filteredItems].sort((a, b) => {
            let res = 0;
            let aString = '';
            let bString = '';
            switch (field) {
                case 'cameraSerial':
                    aString = a.cameraDevice && a.cameraDevice.serialNumber ? a.cameraDevice.serialNumber : '';
                    bString = b.cameraDevice && b.cameraDevice.serialNumber ? b.cameraDevice.serialNumber : '';
                    res = aString.localeCompare(bString);
                    break;
                case 'cameraType':
                    aString =
                        a.cameraDevice && a.cameraDevice.deviceType && a.cameraDevice.deviceType.sysName
                            ? a.cameraDevice.deviceType.sysName
                            : '';
                    bString =
                        b.cameraDevice && b.cameraDevice.deviceType && b.cameraDevice.deviceType.sysName
                            ? b.cameraDevice.deviceType.sysName
                            : '';
                    res = aString.localeCompare(bString);
                    break;
                case 'goSerial':
                    aString =
                        a.vehicle?.telematicDevice && a.vehicle?.telematicDevice.serialNumber
                            ? a.vehicle?.telematicDevice.serialNumber
                            : '';
                    bString =
                        b.vehicle?.telematicDevice && b.vehicle?.telematicDevice.serialNumber
                            ? b.vehicle?.telematicDevice.serialNumber
                            : '';
                    res = aString.localeCompare(bString);
                    break;
                case 'vehicle':
                    aString = a.vehicle && a.vehicle.name ? a.vehicle.name : '';
                    bString = b.vehicle && b.vehicle.name ? b.vehicle.name : '';
                    res = aString.localeCompare(bString);
                    break;
                case 'startDate':
                    const aStart = a.startDate ? moment(a.startDate) : moment();
                    const bStart = b.startDate ? moment(b.startDate) : moment();
                    res = aStart.valueOf() - bStart.valueOf() > 0 ? 1 : aStart.valueOf() - bStart.valueOf() < 0 ? -1 : 0;
                    break;
                case 'lastProcessedEventTime':
                    const alastEventStart =
                        a.cameraDevice && a.cameraDevice.lastProcessedEventTime ? moment(a.cameraDevice.lastProcessedEventTime) : moment();
                    const blastEventStart =
                        b.cameraDevice && b.cameraDevice.lastProcessedEventTime ? moment(b.cameraDevice.lastProcessedEventTime) : moment();
                    res =
                        alastEventStart.valueOf() - blastEventStart.valueOf() > 0
                            ? 1
                            : alastEventStart.valueOf() - blastEventStart.valueOf() < 0
                              ? -1
                              : 0;
                    break;
                case 'isGpsTrackingEnabled':
                    res = a.isGpsTrackingEnabled === b.isGpsTrackingEnabled ? 0 : a.isGpsTrackingEnabled ? -1 : 1;
                    break;
                case 'pairingStatus':
                    aString = a.cameraDevice?.pairingStatus ?? '';
                    bString = b.cameraDevice?.pairingStatus ?? '';
                    res = aString.localeCompare(bString);
                    break;
                case 'connectionStatus':
                    // For asc: Offline (0) first, then Online (1), then by oldest lastHeartbeatTime first
                    const statusOrder = (status: string) => (status === 'Online' ? 1 : 0);
                    const aStatus = statusOrder(a.cameraDevice?.onlineStatus ?? '');
                    const bStatus = statusOrder(b.cameraDevice?.onlineStatus ?? '');
                    if (aStatus !== bStatus) {
                        res = aStatus - bStatus; // Offline first in asc, Online first in desc
                    } else {
                        // If same status, sort by lastHeartbeatTime (oldest first in asc)
                        const aHeartbeat = a.cameraDevice?.lastHeartbeatTime ? moment(a.cameraDevice.lastHeartbeatTime) : moment(0);
                        const bHeartbeat = b.cameraDevice?.lastHeartbeatTime ? moment(b.cameraDevice.lastHeartbeatTime) : moment(0);
                        res = aHeartbeat.valueOf() - bHeartbeat.valueOf();
                    }
                    break;
                default:
                // unhandle //
            }
            return isAsc ? res : -1 * res;
        });

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            sort: field || prevState.sort,
            sortOrder: isAsc ? 'asc' : 'desc',
            filteredItems: sortItems,
            currentPageNumber: preservePage ? prevState.currentPageNumber : 0,
        }));
    };

    const handleVehicleChange = async (event: any): Promise<void> => {
        // get vehicle
        if (event.target.textContent !== '') {
            const { deviceResults } = props.integrationPartnerData.getState();
            const devices = deviceResults.data ? deviceResults.data : [];
            let selectedDevice: TelematicProviderVehicleResponse | undefined = undefined;
            if (props.config.isIntegrationPlatformGeotab()) {
                selectedDevice = devices.find((d) => d.serialNumber === event.target.textContent);
            } else {
                selectedDevice = devices.find((d) => d.name === event.target.textContent);
            }
            if (selectedDevice != undefined) {
                setDeviceAssociationState((prevState) => ({
                    ...prevState,
                    device: selectedDevice,
                    deviceId: selectedDevice?.id,
                }));
            }
        } else {
            setDeviceAssociationState((prevState) => ({
                ...prevState,
                device: undefined,
                deviceId: undefined,
            }));
        }
    };

    const handleCameraChange = async (event: any): Promise<void> => {
        // get camera Id
        const freezer = props.deviceAssociation.getState();
        const { cameraResults } = freezer;
        const cameras = cameraResults.data ? cameraResults.data : [];
        const camera = cameras.find((c) => c.serialNumber === event.target.textContent);
        const cameraId = camera ? (camera.id ? camera.id : 0) : 0;

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            cameraId: cameraId,
        }));
    };

    const toggleOpenPairing = (id?: number, cameraId?: number): void => {
        const { availableDevicesResults } = props.deviceAssociation.getState();
        const { deviceResults } = props.integrationPartnerData.getState();
        const deviceAssociation = availableDevicesResults.data?.find((a) => a.id === id);
        const currentDevice = deviceResults.data?.find((d) => d.id === deviceAssociation?.vehicle?.integrationPartnerVehicleId);
        // set gpsTrackerEnabled: true when toggled open, is encompass and no id (cameraId okay)
        if (!deviceAssociationState.showPairing) {
            // always reset the encompass error to false on open
            setDeviceAssociationState((prevState) => ({
                ...prevState,
                encompassError: false,
            }));
            if (props.config.isIntegrationPlatformEncompass() && (id == undefined || id < 1)) {
                setDeviceAssociationState((prevState) => ({
                    ...prevState,
                    gpsTrackerEnabled: true,
                }));
            }
        }

        const unpairedCameraId = availableDevicesResults.data?.find((a) => a.cameraDevice?.id === cameraId)?.cameraDevice?.id;
        if (cameraId) {
            setDeviceAssociationState((prevState) => ({
                ...prevState,
                showPairing: !deviceAssociationState.showPairing,
                cameraId: unpairedCameraId,
            }));
        } else {
            setDeviceAssociationState((prevState) => ({
                ...prevState,
                showPairing: !deviceAssociationState.showPairing,
                editParingId: id,
                cameraId: deviceAssociation?.cameraDeviceId,
                deviceId: deviceAssociation?.vehicle?.integrationPartnerVehicleId,
                currentVehicleIdForEditing: deviceAssociation?.vehicle?.integrationPartnerVehicleId,
                gpsTrackerEnabled: deviceAssociation?.isGpsTrackingEnabled ?? false,
                // Source of truth for name/telematicDevice; companyLevel here is just a fallback since the backend re-fetches it from Encompass.
                device: currentDevice ?? prevState.device,
            }));
        }
        closeTooltips();
    };

    const toggleOpenRequestVideo = (id?: number): void => {
        const { availableDevicesResults } = props.deviceAssociation.getState();
        const deviceAssociation = availableDevicesResults.data?.find((a) => a.id === id);

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            showRequestVideo: !deviceAssociationState.showRequestVideo,
            editParingId: id,
            cameraId: deviceAssociation?.cameraDeviceId,
            device: deviceAssociation?.vehicle?.integrationPartnerVehicleId,
        }));

        closeTooltips();
    };

    const handleAddPairing = async (): Promise<void> => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            disableAddRemoveButton: true,
        }));

        if (!props.deviceAssociation.freezer.get().availableDevicesResults.isFetching) {
            const device = deviceAssociationState.device;
            const cameraId = deviceAssociationState.cameraId;
            const gpsTrackerEnabled = deviceAssociationState.gpsTrackerEnabled;
            const integrationPartnerVehicleId = deviceAssociationState.deviceId;
            const editPairingId = deviceAssociationState.editParingId;
            // if we have a pairingId, we are editing an existing pairing
            if (editPairingId) {
                try {
                    const vehicle: VehicleRequest = {
                        integrationPartnerVehicleId: integrationPartnerVehicleId,
                        name: device.name,
                        companyLevelId: device.companyLevelId,
                        companyLevelName: device.companyLevelName,
                    };
                    if (props.config.isIntegrationPlatformGeotab()) {
                        vehicle.telematicDevice = {
                            deviceId: device.id,
                            name: device.name,
                            type: device.deviceType,
                            serialNumber: device.serialNumber,
                        };
                    }
                    const editRequest: EditVehicleCameraPairingRequest = {
                        cameraDeviceId: cameraId,
                        vehicle: vehicle,
                        isGpsTrackingEnabled: gpsTrackerEnabled,
                        currentPairingId: editPairingId,
                    };
                    await props.deviceAssociation.putVehicleCameraPairing(editRequest);
                    // fetch the results from the edit and get only the thing we touched
                    const { deviceAssociationResults } = props.deviceAssociation.getState();
                    const updatedPairing = deviceAssociationResults.data?.find((a) => a.id === editPairingId);
                    // if we have the updated pairing, update the filteredItems state
                    if (updatedPairing != null && updatedPairing != undefined) {
                        if (updatedPairing.encompassErrorMessage && updatedPairing.encompassErrorMessage.trim().length > 0) {
                            if (gpsTrackerEnabled) {
                                setDeviceAssociationState((prevState) => ({
                                    ...prevState,
                                    encompassError: true,
                                }));
                                return;
                            } else {
                                // we'll close this dialog and show an error message for failure to turn off gps tracking
                                throw Error('Encompass error message: ' + updatedPairing.encompassErrorMessage);
                            }
                        } else {
                            const { filteredItems } = deviceAssociationState;
                            let updatedItems: VehicleCameraPairingResponse[] = [];
                            filteredItems.forEach((item) => {
                                if (item.id === editPairingId) {
                                    updatedItems.push(updatedPairing);
                                } else {
                                    updatedItems.push(item);
                                }
                            });
                            setDeviceAssociationState((prevState) => ({
                                ...prevState,
                                filteredItems: updatedItems,
                            }));
                        }
                    }
                    setDeviceAssociationState((prevState) => ({
                        ...prevState,
                        openSnackBar: true,
                        alertType: AlertTypes.Success,
                        alertMessage: 'Changes saved successfully.',
                    }));
                    await handleAfterAddingEvent();
                } catch (error) {
                    // TODO: this should be logged elsewhere instead of the console.
                    console.log(error);
                    setDeviceAssociationState((prevState) => ({
                        ...prevState,
                        openSnackBar: true,
                        alertType: AlertTypes.Fail,
                        alertMessage: 'Unable to save changes.',
                    }));
                    await handleAfterAddingEvent();
                }
            } else {
                // if we don't have a pairingId, we are adding a new pairing
                const vehicle: VehicleRequest = {
                    integrationPartnerVehicleId: device.id,
                    name: device.name,
                    companyLevelId: device.companyLevelId,
                    companyLevelName: device.companyLevelName,
                };
                if (props.config.isIntegrationPlatformGeotab()) {
                    vehicle.telematicDevice = {
                        deviceId: device.id,
                        name: device.name,
                        type: device.deviceType,
                        serialNumber: device.serialNumber,
                    };
                }
                const newPairing: VehicleCameraPairingRequest = {
                    vehicle: vehicle,
                    cameraDeviceId: cameraId,
                    isGpsTrackingEnabled: gpsTrackerEnabled,
                };

                if (cameraId !== undefined) {
                    await props.deviceAssociation.postVehicleCameraPairing(newPairing);
                    const { deviceAssociationResults } = props.deviceAssociation.getState();
                    const newlyCreatedPairing = deviceAssociationResults.data?.find(
                        (a) => a.vehicle?.integrationPartnerVehicleId === device.id && a.cameraDeviceId === cameraId,
                    );
                    if (!newlyCreatedPairing) {
                        // check to see if we have an encompass error
                        var encompassError = deviceAssociationResults.data?.find(
                            (a) => a.encompassErrorMessage && a.encompassErrorMessage.trim().length > 0,
                        );
                        // show encompass error if it was due to a gps tracking issue
                        if (encompassError) {
                            setDeviceAssociationState((prevState) => ({
                                ...prevState,
                                encompassError: true,
                            }));
                            return;
                        } else {
                            // not an encompass error, show the snackbar
                            setDeviceAssociationState((prevState) => ({
                                ...prevState,
                                openSnackBar: true,
                                alertType: AlertTypes.Fail,
                                alertMessage: 'Pairing unable to be added.',
                            }));
                        }
                    } else {
                        // success
                        setDeviceAssociationState((prevState) => ({
                            ...prevState,
                            openSnackBar: true,
                            alertType: AlertTypes.Success,
                            alertMessage: 'Pairing added successfully.',
                        }));
                        await handleAfterAddingEvent();
                    }
                } else {
                    // we have form validation here so this shouldn't happen but if it does, show an error message
                    setDeviceAssociationState((prevState) => ({
                        ...prevState,
                        openSnackBar: true,
                        alertType: AlertTypes.Fail,
                        alertMessage: 'Unable to add new pairing: Camera can not be undefined.',
                    }));
                }
            }
        }

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            disableAddRemoveButton: false,
        }));
    };

    const handleDeviceMapping = async (): Promise<void> => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            disableAddRemoveButton: true,
        }));

        // TODO This process should be move to a background process (Gerald's suggestion) TBD
        if (!props.deviceAssociation.freezer.get().availableDevicesResults.isFetching) {
            const { deviceResults } = props.integrationPartnerData.getState();
            const devices = deviceResults.data ? deviceResults.data : [];

            const vehiclesData: VehicleRequest[] = devices.map((d) => ({ integrationPartnerVehicleId: d.id, name: d.name }));

            if (vehiclesData !== undefined) {
                await props.deviceAssociation.mapDeviceAssociations(vehiclesData);
            } else {
                // TODO: this should be logged elsewhere instead of the console.
                console.error('Unable to map device names, couldnt find integrationpartner data.');
            }
        }
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            disableAddRemoveButton: false,
        }));
    };

    const toggleDeletePairing = (id: number | undefined, telematicDeviceId: string | undefined): void => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            showDelete: !deviceAssociationState.showDelete,
            deletePairingId: id,
            deletePairingIdTelematicDeviceId: telematicDeviceId,
        }));

        closeTooltips();
    };

    const resetTableItems = () => {
        const { availableDevicesResults } = props.deviceAssociation.getState();
        const items = availableDevicesResults.data ? availableDevicesResults.data : [];

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            allItems: items,
            filteredItems: items,
        }));
    };

    const handlePageChange = (event: any, newPage: number) => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            currentPageNumber: newPage,
        }));
    };

    const handleRowsPerPageChange = (event: any) => {
        const newRowsPerPage = Number(event.target.value);

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            rowsPerPage: newRowsPerPage,
            currentPageNumber: 0,
        }));
    };

    const handleDeletePairing = async (): Promise<void> => {
        if (!props.deviceAssociation.freezer.get().availableDevicesResults.isFetching) {
            await props.deviceAssociation.endVehicleCameraPairing(deviceAssociationState.deletePairingId || 0);

            // fetch the results from the edit and get only the thing we touched
            const { deviceAssociationResults } = props.deviceAssociation.getState();
            const deletePairing = deviceAssociationResults.data?.find((a) => a.id === deviceAssociationState.deletePairingId);
            // if we have the updated pairing, it means we didn't delete it
            if (deletePairing != null && deletePairing != undefined) {
                if (deletePairing.encompassErrorMessage && deletePairing.encompassErrorMessage.trim().length > 0) {
                    setDeviceAssociationState((prevState) => ({
                        ...prevState,
                        openSnackBar: true,
                        alertType: AlertTypes.Fail,
                        alertMessage: 'Unable to save changes.',
                    }));
                }
            }

            setDeviceAssociationState((prevState) => ({
                ...prevState,
                showDelete: false,
                deletePairingId: undefined,
                deletePairingIdTelematicDeviceId: undefined,
                selectedVehiclesFilter: prevState.selectedVehiclesFilter.filter((id) => id !== prevState.deletePairingIdTelematicDeviceId),
            }));

            await props.deviceAssociation.getAvailableDevices();
            await props.deviceAssociation.getUnpairedCameraDevices();
        }

        resetTableItems();
    };

    const handleOpenTooltip = (idx: number): void => {
        let tooltips = _.clone(deviceAssociationState.openTooltips);

        if (tooltips[idx]) {
            tooltips[idx] = false;
        } else {
            tooltips = tooltips.filter((t) => t).map((t) => (t = false));
            tooltips[idx] = tooltips[idx] ? false : true;
        }

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            openTooltips: tooltips,
        }));
    };

    const handleConfirmLiveVideo = (openLiveVideo: boolean, vehicleCameraPairingId: number | undefined) => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            showConfirmLiveVideo: openLiveVideo,
            vehicleCameraPairingSelected: vehicleCameraPairingId,
        }));

        closeTooltips();
    };

    const handleShowLiveVideo = (showLiveVideo: boolean, alertDriver: boolean) => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            showLiveVideo,
            alertDriver,
            showConfirmLiveVideo: false,
        }));
    };

    const closeTooltips = (): void => {
        let tooltips = _.clone(deviceAssociationState.openTooltips);
        tooltips = tooltips.map((t) => (t = false));
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            openTooltips: tooltips,
        }));
    };

    const handleGpsTrackerChange = (event: any): void => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            gpsTrackerEnabled: event.target.checked,
            encompassError: false,
        }));
    };

    //#region Internal

    const applyFilters = (vehicleIds?: string[], serial?: string, pairingStatus?: string[]): VehicleCameraPairingResponse[] => {
        const { allItems } = deviceAssociationState;
        const filterVehicleIds = vehicleIds || deviceAssociationState.selectedVehiclesFilter;
        const filterSerial = serial !== undefined ? serial.toUpperCase().trim() : deviceAssociationState.serialFilter.toUpperCase().trim();
        const filterPairing = pairingStatus || deviceAssociationState.selectedPairingStatusFilter;
        let filteredItems = allItems;

        if (filterVehicleIds.length) {
            filteredItems = filteredItems.filter(
                (x) => x.vehicle?.integrationPartnerVehicleId && filterVehicleIds.indexOf(x.vehicle.integrationPartnerVehicleId) > -1,
            );
        }

        if (filterSerial) {
            filteredItems = filteredItems.filter(
                (x) =>
                    (x.cameraDevice?.serialNumber && x.cameraDevice.serialNumber.toUpperCase().indexOf(filterSerial) > -1) ||
                    (props.config.isIntegrationPlatformGeotab() &&
                        x.vehicle?.telematicDevice?.serialNumber &&
                        x.vehicle?.telematicDevice?.serialNumber.toUpperCase().indexOf(filterSerial) > -1),
            );
        }

        if (filterPairing.length > 0) {
            filteredItems = filteredItems.filter(
                (x) => x.cameraDevice?.pairingStatus && filterPairing.indexOf(x.cameraDevice?.pairingStatus) > -1,
            );
        }

        return filteredItems;
    };

    const mapDeviceList = (devices: any[]): IDropdownItem[] => {
        const devicesList: IDropdownItem[] = devices
            .filter(({ vehicle }) => vehicle && vehicle.name && vehicle.integrationPartnerVehicleId)
            .map(({ vehicle }) => ({
                label: vehicle.name,
                value: vehicle.integrationPartnerVehicleId,
            }));

        return SortDropdownItems(devicesList);
    };

    const mapPairingStatuses = (): IDropdownItem[] => {
        const pairingStatus: IDropdownItem[] = [
            {
                label: 'Paired',
                value: 'Paired',
            },
            {
                label: 'Unpaired',
                value: 'Unpaired',
            },
        ];

        return SortDropdownItems(pairingStatus);
    };

    const handleAfterAddingEvent = async () => {
        setDeviceAssociationState((prevState) => ({
            ...prevState,
            showPairing: false,
            device: {},
            cameraId: undefined,
            gpsTrackerEnabled: false,
            deviceId: undefined,
            editParingId: undefined,
        }));

        await props.deviceAssociation.getAvailableDevices();
        await props.deviceAssociation.getUnpairedCameraDevices();

        resetTableItems();
    };
    //#endregion

    const { getSplitTreatment } = useGetSplitTreatment();

    const { cameraResults, availableDevicesResults } = props.deviceAssociation.getState();
    const { selectedVehiclesFilter, serialFilter, allItems, selectedPairingStatusFilter } = deviceAssociationState;

    const cameras = cameraResults.data ? cameraResults.data : [];
    const deviceAssociations = availableDevicesResults.data ? availableDevicesResults.data : [];
    // Derived fresh from the live cache every render (rather than a snapshot captured once when the drawer
    // opened) so a status change picked up by a refetch -- e.g. the camera going offline mid-preview --
    // shows up immediately without needing to close and reopen the drawer.
    const requestVideoPairing = availableDevicesResults.data?.find((a) => a.id === deviceAssociationState.editParingId);

    const { deviceResults, groupsFilter } = props.integrationPartnerData.getState();
    const isEncompass = props.config.isIntegrationPlatformEncompass();
    //For standalone, Navbar takes up 305px so we can set tablet view on smaller screen sizes if the navbar is hidden
    const isTabletView = isEncompass
        ? useMediaQuery({
              minWidth: props.config.isRunningStandAlone() ? 886 : 477, // 477px = 581 (max width mobile resolution) - 104 (encompass navbar width)
              maxWidth: props.config.isRunningStandAlone() ? 1205 : 796, // 796px = 900 (max width tablet resolution) - 104 (encompass navbar width)
          })
        : false;
    const isMobileView = isEncompass
        ? useMediaQuery({
              maxWidth: props.config.isRunningStandAlone() ? 886 : 581, //encompass navbar is removed here and we don't need to account for it
          })
        : false;

    const [openFilterDevicesPanel, setOpenFilterDevicesPanel] = useState(false);

    const [cameraLiveVideoSplitEnabled, setCameraLiveVideoSplitEnabled] = useState(false);
    const [deviceAssociationsReadOnly, setDeviceAssociationsReadOnly] = useState(false);
    const [videoRecallPreviewEnabled, setVideoRecallPreviewEnabled] = useState(false);

    useEffect(() => {
        const getSplitStatus = async () => {
            const cameraLiveVideoEnabled = await getSplitTreatment(splitTreatmentNames.cameraLiveVideo);
            const deviceAssociationsReadOnlyEnabled = await getSplitTreatment(splitTreatmentNames.deviceAssociationsReadOnly);
            const videoRecallPreviewSplitEnabled = await getSplitTreatment(splitTreatmentNames.videoRecallPreview);

            setCameraLiveVideoSplitEnabled(cameraLiveVideoEnabled);
            setDeviceAssociationsReadOnly(deviceAssociationsReadOnlyEnabled);
            setVideoRecallPreviewEnabled(videoRecallPreviewSplitEnabled);
        };

        getSplitStatus().catch(() => {
            setCameraLiveVideoSplitEnabled(false);
            setDeviceAssociationsReadOnly(false);
            setVideoRecallPreviewEnabled(false);
        });
    }, []);

    //Note: we may want to filter out inactive/disposed devices here to hide them altogether on this page, once there's a process in place to unpair disposed/inactive devices automatically.
    //For now, we'll want them to show up in case the vehicle is disposed in Encompass or inactive in Geotab, so we can manually unpair if needed
    const devices = deviceResults.data ? deviceResults.data : [];
    const vehicles = useMemo(() => {
        return mapDeviceList(allItems);
    }, [allItems]);

    const pairingStatuses = mapPairingStatuses();

    const editableVehiclesForPairing = devices
        .filter(
            (d) =>
                deviceAssociations.findIndex((pairing) => pairing.vehicle?.integrationPartnerVehicleId === d.id) < 0 ||
                d.id === deviceAssociationState.currentVehicleIdForEditing,
        )
        .filter((d) => d.isActive);

    const rowHeaders = [
        {
            id: 'cameraSerial',
            label: 'Camera Serial #',
        },
        {
            id: 'cameraType',
            label: 'Camera Device Type',
        },
        {
            id: 'vehicle',
            label: 'Paired Vehicle',
        },
        {
            id: 'startDate',
            label: 'Date Paired',
        },
        {
            id: 'lastProcessedEventTime',
            label: 'Last Processed Event',
        },
        {
            id: 'pairingStatus',
            label: 'Pairing Status',
        },
        {
            id: 'connectionStatus',
            label: 'Connection Status',
            icon: <FontAwesomeIcon icon={faInfoCircle} className={styles.infoButton} />,
        },
    ];

    // Go Device columns added for GeoTab
    if (props.config.isIntegrationPlatformGeotab()) {
        rowHeaders.splice(2, 0, { id: 'goSerial', label: 'GO Serial #' });
    }

    // IsGpsTrackingEnabled for encompass
    if (isEncompass) {
        rowHeaders.splice(3, 0, { id: 'isGpsTrackingEnabled', label: 'GPS Tracker' });
    }

    const handleGroupFilterApplied = async () => {
        await IntegrationPartnerDataService.getDevices();
        await DeviceAssociationService.getAvailableDevices();
        // Only include selectedValues that exist in the vehicle list
        const { deviceResults } = props.integrationPartnerData.getState();
        const vehicles = deviceResults.data ? deviceResults.data : [];

        setDeviceAssociationState((prevState) => ({
            ...prevState,
            selectedVehiclesFilter: prevState.selectedVehiclesFilter.filter((v) => vehicles.findIndex((l) => l.id === v) > -1),
        }));
    };

    const handleOpenFilterDevicesPanel = () => {
        setOpenFilterDevicesPanel(true);
    };

    const handleCloseFilterDevicesPanel = () => {
        setOpenFilterDevicesPanel(false);
    };

    useEffect(() => {
        reApplyFilters();
    }, [deviceAssociationState.allItems, vehicles, selectedVehiclesFilter]);

    // allItems/filteredItems are a point-in-time copy of availableDevicesResults, not a live view of it, so
    // this resyncs the table whenever that underlying cache changes -- including the refetch triggered when
    // the Request Video preview detects a camera went offline mid-load.
    useEffect(() => {
        resetTableItems();
    }, [availableDevicesResults.data]);

    useEffect(() => {
        // if a vehicle is unpaired its removed from the Vehicles Drop Down. We need to sync the selections so the Drop Down is updated.
        syncSelectedVehicleFilter(
            selectedVehiclesFilter,
            vehicles.map((v) => v.value),
        );
    }, [vehicles]);

    useEffect(() => {
        handleGroupFilterApplied().then(() => {
            resetTableItems();
        });
    }, [groupsFilter]);

    if (isEncompass) {
        useHorizontalDragScroll(containerRef as MutableRefObject<HTMLElement | null>, '.drag-scroll-active', 'drag-scroll-container');
    }

    const exportButton = (
        <DeviceAssociationExport
            csvTitle={deviceAssociationState.csvTitle}
            filteredItems={deviceAssociationState.filteredItems}
            isMobileView={isMobileView}
        />
    );

    const StopSortBubble: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
        const stop = (e: React.SyntheticEvent) => {
            e.stopPropagation();
        };
        const stopKey = (e: React.KeyboardEvent) => {
            // prevent Space/Enter from triggering sort via key events
            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        };
        return (
            <span onClick={stop} onMouseDown={stop} onTouchStart={stop} onKeyDown={stopKey}>
                {children}
            </span>
        );
    };

    return (
        <div>
            <div className={styles.main}>
                <TermsOfUse />

                <PageHeader pageTitle={'Device Associations'} fixedHeight={false} />

                {deviceAssociationState.openSnackBar && (
                    <AlertMessage
                        message={deviceAssociationState.alertMessage}
                        openSnackbar={deviceAssociationState.openSnackBar}
                        alertType={deviceAssociationState.alertType}
                        showIcon={isEncompass}
                        isUndo={true}
                        autoHide={true}
                        handleSnackClose={(): void => setDeviceAssociationState((prevState) => ({ ...prevState, openSnackBar: false }))}
                        handleSubmit={(): void => setDeviceAssociationState((prevState) => ({ ...prevState, openSnackBar: false }))}
                    />
                )}
                <ConfigurableFilterBar
                    filters={getDeviceAssociationFilterConfig()}
                    onFilterChange={handleDeviceAssociationFilterChange}
                    isMobileView={isMobileView}
                    slotProps={{
                        mobile: {
                            left: <FiltersButton onClick={handleOpenFilterDevicesPanel} />,
                            right: exportButton,
                        },
                        desktop: {
                            right: exportButton,
                        },
                    }}
                    mobileProps={{
                        drawerIsOpen: openFilterDevicesPanel,
                        onCloseDrawer: handleCloseFilterDevicesPanel,
                        drawerTitle: 'Filter Devices',
                    }}
                />
                {deviceAssociationState.filteredItems.length === 0 && (
                    <div className={styles.noAssociations}>
                        {deviceAssociationState.devicesLoaded ? (
                            <div>No Device Associations found for the criteria selected</div>
                        ) : (
                            <CircularProgress></CircularProgress>
                        )}
                    </div>
                )}
                {isEncompass && deviceAssociationState.filteredItems.length > 0 ? (
                    <div>
                        <TableContainer
                            id="drag-scroll-container"
                            ref={containerRef}
                            className={isTabletView || isMobileView ? styles.dragScrollActive : ''}
                            style={{ maxHeight: 'calc(100vh - 260px)' }}
                        >
                            <Table stickyHeader className={styles.devicesTable}>
                                <TableHead>
                                    <TableRow className={styles.headerRow}>
                                        {rowHeaders.map((head) => {
                                            return head.icon ? (
                                                <TableCell key={head.id} className={styles.minWidthHeaderCell}>
                                                    <TableSortLabel
                                                        key={head.id}
                                                        className={styles.sortLabel}
                                                        active={deviceAssociationState.sort === head.id}
                                                        direction={deviceAssociationState.sortOrder}
                                                        onClick={() => handleRequestSort(head.id)}
                                                    >
                                                        <div className={styles.connectionStatusHeader}>
                                                            <div className={styles.connectionStatusHeaderLabel}>{head.label}</div>

                                                            {head.icon ? (
                                                                <StopSortBubble>
                                                                    <ConnectionStatusTooltip hoverTooltipDisabled />
                                                                </StopSortBubble>
                                                            ) : null}
                                                        </div>
                                                    </TableSortLabel>
                                                </TableCell>
                                            ) : (
                                                <TableCell
                                                    key={head.id}
                                                    className={styles.minWidthHeaderCell}
                                                    sortDirection={
                                                        deviceAssociationState.sort === head.id ? deviceAssociationState.sortOrder : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={deviceAssociationState.sort === head.id}
                                                        direction={deviceAssociationState.sortOrder}
                                                        onClick={(): void => handleRequestSort(head.id)}
                                                        className={styles.sortLabel}
                                                    >
                                                        {head.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            );
                                        })}
                                        {/* Pinned Options column header */}
                                        <TableCell
                                            sx={
                                                isTabletView || isMobileView
                                                    ? {
                                                          position: 'sticky',
                                                          right: 0,
                                                          backgroundColor: 'white',
                                                          boxShadow:
                                                              '3px 4px 0px white,0px 1px 0px white,-6px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {/* Extra header cell for the options column to prevent weird gap while scrolling with sticky header */}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {deviceAssociationState.filteredItems
                                        .slice(
                                            deviceAssociationState.currentPageNumber * deviceAssociationState.rowsPerPage,
                                            deviceAssociationState.currentPageNumber * deviceAssociationState.rowsPerPage +
                                                deviceAssociationState.rowsPerPage,
                                        )
                                        .map((pairing: VehicleCameraPairingResponse, idx) => {
                                            const cameraDevice = pairing.cameraDevice || {};
                                            const cameraType = cameraDevice.deviceType || {};
                                            const vehicle = pairing.vehicle || {};
                                            const isPaired = cameraDevice.pairingStatus?.toLowerCase() === 'paired';
                                            return (
                                                <TableRow key={idx} className={styles.bodyRow}>
                                                    <TableCell className={styles.minWidthBodyCell}>{cameraDevice.serialNumber}</TableCell>
                                                    <TableCell className={styles.minWidthBodyCell}>{cameraType.displayName}</TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>{vehicle.name}</TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>
                                                        {pairing.isGpsTrackingEnabled ? 'On' : 'Off'}
                                                    </TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>
                                                        {pairing.startDate ? DateFormatter.date(moment(pairing.startDate)) : ''}
                                                    </TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>
                                                        {cameraDevice.lastProcessedEventTime
                                                            ? DateFormatter.dateAndTimezone(moment(cameraDevice.lastProcessedEventTime))
                                                            : ''}
                                                    </TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>{cameraDevice.pairingStatus}</TableCell>

                                                    <TableCell className={styles.minWidthBodyCell}>
                                                        <div
                                                            className={`${
                                                                cameraDevice.onlineStatus === 'Online' ? styles.onlineStreamaxStatus : ''
                                                            }`}
                                                        >
                                                            {cameraDevice.onlineStatus}
                                                        </div>
                                                        <div className={styles.connectionStatusDate}>
                                                            {cameraDevice.lastHeartbeatTime ? (
                                                                DateFormatter.dateAndTimezone(moment(cameraDevice.lastHeartbeatTime))
                                                            ) : (
                                                                <>&mdash;</>
                                                            )}
                                                        </div>
                                                    </TableCell>

                                                    {/* Pinned Options column */}
                                                    <TableCell
                                                        sx={
                                                            isTabletView || isMobileView
                                                                ? {
                                                                      position: 'sticky',
                                                                      right: 0,
                                                                      backgroundColor: 'white',
                                                                      boxShadow:
                                                                          '3px 4px 0px white,0px 1px 0px white,-6px 0px 4px 0px rgba(0, 0, 0, 0.05)',
                                                                  }
                                                                : undefined
                                                        }
                                                    >
                                                        <div className={styles.encompassOptionColumn}>
                                                            {!isPaired && deviceAssociationsReadOnly ? null : (
                                                                <EllipsisPopperMenu
                                                                    items={
                                                                        !isPaired
                                                                            ? ([
                                                                                  {
                                                                                      key: 'pair',
                                                                                      text: 'Pair',
                                                                                      onClick: () =>
                                                                                          toggleOpenPairing(
                                                                                              pairing.id,
                                                                                              isPaired ? undefined : cameraDevice?.id,
                                                                                          ),
                                                                                  },
                                                                              ] as EllipsisItem[])
                                                                            : ([
                                                                                  ...(!deviceAssociationsReadOnly
                                                                                      ? [
                                                                                            {
                                                                                                key: 'editPairing',
                                                                                                text: 'Edit Pairing',
                                                                                                onClick: () =>
                                                                                                    toggleOpenPairing(
                                                                                                        pairing.id,
                                                                                                        isPaired
                                                                                                            ? undefined
                                                                                                            : cameraDevice?.id,
                                                                                                    ),
                                                                                            },
                                                                                        ]
                                                                                      : []),
                                                                                  {
                                                                                      key: 'liveVideo',
                                                                                      text: 'View Live Video',
                                                                                      isHidden: !cameraLiveVideoSplitEnabled,
                                                                                      onClick: () =>
                                                                                          handleConfirmLiveVideo(true, pairing.id),
                                                                                  },
                                                                                  {
                                                                                      key: 'requestVideo',
                                                                                      text: 'Request Video',
                                                                                      onClick: () => toggleOpenRequestVideo(pairing.id),
                                                                                  },
                                                                                  ...(!deviceAssociationsReadOnly
                                                                                      ? [
                                                                                            {
                                                                                                key: 'removePairing',
                                                                                                text: 'Remove Pairing',
                                                                                                onClick: () =>
                                                                                                    toggleDeletePairing(
                                                                                                        pairing.id,
                                                                                                        pairing.vehicle?.telematicDevice
                                                                                                            ?.deviceId,
                                                                                                    ),
                                                                                            },
                                                                                        ]
                                                                                      : []),
                                                                              ] as EllipsisItem[])
                                                                    }
                                                                    isEncompass
                                                                />
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[10, 20, 50]}
                            count={deviceAssociationState.filteredItems.length}
                            rowsPerPage={deviceAssociationState.rowsPerPage}
                            page={deviceAssociationState.currentPageNumber}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            slotProps={{
                                select: {
                                    MenuProps: {
                                        //these props prevent the pagination dropdown from causing scrolling jumping when clicked
                                        disableScrollLock: true,
                                        autoFocus: false,
                                        disableAutoFocusItem: true,
                                        disableEnforceFocus: true,
                                        disableAutoFocus: true,
                                    },
                                },
                            }}
                        />
                    </div>
                ) : (
                    deviceAssociationState.filteredItems.length > 0 && (
                        <div>
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {rowHeaders.map((head) => {
                                                return head.icon ? (
                                                    <TableCell key={head.id} className={styles.headerCell}>
                                                        <TableSortLabel
                                                            key={head.id}
                                                            className={styles.sortLabel}
                                                            active={deviceAssociationState.sort === head.id}
                                                            direction={deviceAssociationState.sortOrder}
                                                            onClick={(): void => handleRequestSort(head.id)}
                                                        >
                                                            <div className={styles.connectionStatusHeader}>
                                                                <div className={styles.connectionStatusHeaderLabel}>{head.label}</div>
                                                                {head.icon ? <ConnectionStatusTooltip /> : null}
                                                            </div>
                                                        </TableSortLabel>
                                                    </TableCell>
                                                ) : (
                                                    <TableCell
                                                        key={head.id}
                                                        className={styles.headerCell}
                                                        sortDirection={
                                                            deviceAssociationState.sort === head.id
                                                                ? deviceAssociationState.sortOrder
                                                                : false
                                                        }
                                                    >
                                                        <TableSortLabel
                                                            active={deviceAssociationState.sort === head.id}
                                                            direction={deviceAssociationState.sortOrder}
                                                            onClick={(): void => handleRequestSort(head.id)}
                                                            className={styles.sortLabel}
                                                        >
                                                            {head.label}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deviceAssociationState.filteredItems
                                            .slice(
                                                deviceAssociationState.currentPageNumber * deviceAssociationState.rowsPerPage,
                                                deviceAssociationState.currentPageNumber * deviceAssociationState.rowsPerPage +
                                                    deviceAssociationState.rowsPerPage,
                                            )
                                            .map((pairing: VehicleCameraPairingResponse, idx) => {
                                                const cameraDevice = pairing.cameraDevice || {};
                                                const cameraType = cameraDevice.deviceType || {};
                                                const vehicle = pairing.vehicle || {};
                                                const isPaired = cameraDevice.pairingStatus?.toLowerCase() === 'paired';
                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell>{cameraDevice.serialNumber}</TableCell>
                                                        <TableCell>{cameraType.displayName}</TableCell>
                                                        {props.config.isIntegrationPlatformGeotab() && (
                                                            <TableCell className={styles.bodyCell}>
                                                                {vehicle?.telematicDevice ? vehicle?.telematicDevice.serialNumber : ''}
                                                            </TableCell>
                                                        )}
                                                        <TableCell className={styles.bodyCell}>{vehicle.name}</TableCell>
                                                        <TableCell>
                                                            {pairing.startDate ? DateFormatter.date(moment(pairing.startDate)) : ''}
                                                        </TableCell>

                                                        <TableCell className={styles.bodyCell}>
                                                            {cameraDevice.lastProcessedEventTime
                                                                ? DateFormatter.dateAndTimezone(moment(cameraDevice.lastProcessedEventTime))
                                                                : ''}
                                                        </TableCell>

                                                        <TableCell>{cameraDevice.pairingStatus}</TableCell>

                                                        <TableCell className={styles.bodyCell}>
                                                            <div
                                                                className={`${
                                                                    cameraDevice.onlineStatus === 'Online'
                                                                        ? styles.onlineStreamaxStatus
                                                                        : ''
                                                                }`}
                                                            >
                                                                {cameraDevice.onlineStatus}
                                                            </div>
                                                            <div className={styles.connectionStatusDate}>
                                                                {cameraDevice.lastHeartbeatTime ? (
                                                                    DateFormatter.dateAndTimezone(moment(cameraDevice.lastHeartbeatTime))
                                                                ) : (
                                                                    <>&mdash;</>
                                                                )}
                                                            </div>
                                                        </TableCell>

                                                        <TableCell className={styles.optionsEllipsisColumn}>
                                                            <EllipsisPopperMenu
                                                                items={
                                                                    !isPaired
                                                                        ? ([
                                                                              {
                                                                                  key: 'pair',
                                                                                  text: 'Pair',
                                                                                  onClick: () =>
                                                                                      toggleOpenPairing(
                                                                                          pairing.id,
                                                                                          isPaired ? undefined : cameraDevice?.id,
                                                                                      ),
                                                                              },
                                                                          ] as EllipsisItem[])
                                                                        : ([
                                                                              {
                                                                                  key: 'editPairing',
                                                                                  text: 'Edit Pairing',
                                                                                  onClick: () =>
                                                                                      toggleOpenPairing(
                                                                                          pairing.id,
                                                                                          isPaired ? undefined : cameraDevice?.id,
                                                                                      ),
                                                                              },
                                                                              {
                                                                                  key: 'liveVideo',
                                                                                  text: 'View Live Video',
                                                                                  isHidden: !cameraLiveVideoSplitEnabled,
                                                                                  onClick: () => handleConfirmLiveVideo(true, pairing.id),
                                                                              },
                                                                              {
                                                                                  key: 'requestVideo',
                                                                                  text: 'Request Video',
                                                                                  onClick: () => toggleOpenRequestVideo(pairing.id),
                                                                              },
                                                                              {
                                                                                  key: 'removePairing',
                                                                                  text: 'Remove Pairing',
                                                                                  onClick: () =>
                                                                                      toggleDeletePairing(
                                                                                          pairing.id,
                                                                                          pairing.vehicle?.telematicDevice?.deviceId,
                                                                                      ),
                                                                              },
                                                                          ] as EllipsisItem[])
                                                                }
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[10, 20, 50]}
                                count={deviceAssociationState.filteredItems.length}
                                rowsPerPage={deviceAssociationState.rowsPerPage}
                                page={deviceAssociationState.currentPageNumber}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                            />
                        </div>
                    )
                )}
            </div>

            <PairingDialog
                open={deviceAssociationState.showPairing}
                onClose={() => toggleOpenPairing()}
                vehicles={editableVehiclesForPairing}
                cameras={
                    deviceAssociationState.editParingId
                        ? [deviceAssociations.find((da) => da.id === deviceAssociationState.editParingId)?.cameraDevice]
                        : cameras
                }
                handleVehicleChange={handleVehicleChange}
                device={deviceAssociationState.deviceId}
                handleCameraChange={handleCameraChange}
                cameraId={deviceAssociationState.cameraId}
                isEdit={deviceAssociationState.editParingId !== undefined}
                handleAddPairing={handleAddPairing}
                config={props.config}
                handleGpsTrackerChange={handleGpsTrackerChange}
                isGpsTracker={deviceAssociationState.gpsTrackerEnabled}
                hasEncompassError={deviceAssociationState.encompassError}
            ></PairingDialog>

            {videoRecallPreviewEnabled ? (
                <RequestVideoDrawer
                    open={deviceAssociationState.showRequestVideo}
                    onClose={() => toggleOpenRequestVideo()}
                    pairings={requestVideoPairing ? [requestVideoPairing] : []}
                    preselectedPairingId={requestVideoPairing?.id}
                    config={props.config}
                />
            ) : (
                <VideoRequestDialog
                    open={deviceAssociationState.showRequestVideo}
                    onClose={() => toggleOpenRequestVideo()}
                    config={props.config}
                    device={deviceAssociationState.device}
                    isMobileView={isMobileView}
                ></VideoRequestDialog>
            )}

            <Dialog open={deviceAssociationState.showDelete} className={styles.dialog}>
                <DialogTitle sx={{ padding: '24px', lineHeight: '100%' }}>Remove Association?</DialogTitle>
                <DialogActions sx={{ padding: '8px 24px 24px 24px' }}>
                    <Button sx={{ p: 0 }} onClick={(): void => toggleDeletePairing(undefined, undefined)}>
                        Cancel
                    </Button>
                    <Button sx={{ p: 0 }} disabled={deviceAssociationState.disableAddRemoveButton} onClick={handleDeletePairing}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deviceAssociationState.showLiveVideo}
                maxWidth={'lg'}
                PaperProps={{
                    sx:
                        isMobileView || isTabletView
                            ? {
                                  width: '90%',
                                  minWidth: 355,
                              }
                            : {},
                }}
            >
                <DialogContent className={isMobileView || isTabletView ? styles.liveVideoMobileDialog : styles.liveVideoDialog}>
                    <LiveVideo
                        vehicleCameraPairingId={deviceAssociationState.vehicleCameraPairingSelected}
                        alertDriver={deviceAssociationState.alertDriver}
                        onClose={() => handleShowLiveVideo(false, false)}
                        isMobileView={isMobileView || isTabletView}
                    />
                </DialogContent>
            </Dialog>

            {deviceAssociationState.showConfirmLiveVideo && (
                <ConfirmLiveVideo
                    isOpen={deviceAssociationState.showConfirmLiveVideo}
                    handleShowLiveVideo={handleShowLiveVideo}
                    handleClose={handleConfirmLiveVideo}
                    isMobileView={isMobileView}
                />
            )}
        </div>
    );
};

export const DeviceAssociation = ConfigService.inject(
    IntegrationPartnerDataService.inject(DeviceAssociationService.inject(_DeviceAssociation)),
);
